import { openDB } from 'idb';

const DB_NAME = 'syncvault-local';
const DB_VERSION = 5;

/**
 * Initialize the IndexedDB database for local caching and offline support.
 */
export const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db, oldVersion) {
    // Store for clipboard items (clips)
    if (!db.objectStoreNames.contains('clips')) {
      const clipsStore = db.createObjectStore('clips', { keyPath: 'id' });
      clipsStore.createIndex('created_at', 'created_at');
    }

    // Store for notes and sub-pages
    if (!db.objectStoreNames.contains('notes')) {
      const notesStore = db.createObjectStore('notes', { keyPath: 'id' });
      notesStore.createIndex('updated_at', 'updated_at');
      notesStore.createIndex('folder_id', 'folder_id');
      notesStore.createIndex('parent_id', 'parent_id');
    }

    // Store for folders
    if (!db.objectStoreNames.contains('folders')) {
      const foldersStore = db.createObjectStore('folders', { keyPath: 'id' });
      foldersStore.createIndex('parent_id', 'parent_id');
    }

    // Store for pending sync actions (offline support)
    if (!db.objectStoreNames.contains('sync_queue')) {
      // We use auto-incrementing ID for the queue to maintain order
      db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
    }

    // NEW: Store for user session (auth tokens and profile)
    if (!db.objectStoreNames.contains('session')) {
      db.createObjectStore('session');
    }

    // NEW: Store for snaps (image blobs)
    if (!db.objectStoreNames.contains('snaps')) {
      const snapsStore = db.createObjectStore('snaps', { keyPath: 'id' });
      snapsStore.createIndex('created_at', 'created_at');
    }

    // NEW: Store for devices
    if (!db.objectStoreNames.contains('devices')) {
      const devicesStore = db.createObjectStore('devices', { keyPath: 'id' });
      devicesStore.createIndex('last_seen', 'last_seen');
    }

    // NEW: Key-value cache store (dashboard counts, etc.)
    if (!db.objectStoreNames.contains('app_cache')) {
      db.createObjectStore('app_cache');
    }
  },
});

/**
 * Service object for interacting with the local IndexedDB.
 */
export const localDB = {
  // ... existing methods ...
  async getAll(storeName) {
    return (await dbPromise).getAll(storeName);
  },

  async get(storeName, id) {
    return (await dbPromise).get(storeName, id);
  },

  async put(storeName, data, key) {
    return (await dbPromise).put(storeName, data, key);
  },

  async delete(storeName, id) {
    return (await dbPromise).delete(storeName, id);
  },

  async clear(storeName) {
    return (await dbPromise).clear(storeName);
  },

  /**
   * Session Management
   */
  async setSession(sessionData) {
    return (await dbPromise).put('session', sessionData, 'current');
  },

  async getSession() {
    return (await dbPromise).get('session', 'current');
  },

  async clearSession() {
    return (await dbPromise).delete('session', 'current');
  },

  /**
   * Add an action to the synchronization queue.
   * @param {Object} action - The action to queue (e.g., { type: 'CREATE_NOTE', data: { ... } })
   */
  async addToQueue(action) {
    const entry = {
      ...action,
      timestamp: Date.now(),
    };
    return (await dbPromise).add('sync_queue', entry);
  },

  /**
   * Retrieve all pending actions from the sync queue.
   */
  async getQueue() {
    // Result is ordered by 'id' (insertion order) due to auto-increment
    return (await dbPromise).getAll('sync_queue');
  },

  /**
   * Remove a completed or failed action from the queue.
   */
  async removeFromQueue(queueId) {
    return (await dbPromise).delete('sync_queue', queueId);
  },

  /**
   * App Cache (key-value store for dashboard counts, etc.)
   */
  async setCache(key, value) {
    return (await dbPromise).put('app_cache', value, key);
  },

  async getCache(key) {
    return (await dbPromise).get('app_cache', key);
  },

  /**
   * Remove all queue entries that reference a given tempId.
   * Used when a temp-only clip is deleted before it was ever synced.
   */
  async removeQueueEntriesByTempId(tempId) {
    const db = await dbPromise;
    const tx = db.transaction('sync_queue', 'readwrite');
    const store = tx.objectStore('sync_queue');
    const all = await store.getAll();
    for (const entry of all) {
      if (entry.tempId === tempId) {
        await store.delete(entry.id);
      }
    }
    await tx.done;
  }
};
